import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthStore from '../stores/authStore';
import Footer from '../components/home/Footer';
import './Payment.css';

function Payment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [selectedCrypto, setSelectedCrypto] = useState('eth');
  const [email, setEmail] = useState(user?.email || '');

  const levelId = searchParams.get('level');

  const plans = [
    { level: 1, name: 'Level 1', price: 0 },
    { level: 25, name: 'Level 25', price: 25 },
    { level: 50, name: 'Level 50', price: 50 },
    { level: 100, name: 'Level 100', price: 100 },
    { level: 5000, name: 'MVP Development', price: 5000 },
    { level: 10000, name: 'Advanced MVP', price: 10000 }
  ];

  const cryptoOptions = [
    { id: 'eth', name: 'ETH', symbol: 'ERC20', icon: '⟠' },
    { id: 'bnb', name: 'BNB', symbol: 'BEP20', icon: '🟡' },
    { id: 'usdt', name: 'USDT', symbol: 'ERC20', icon: '🔷' }
  ];

  const selectedPlan = plans.find(p => p.level === parseInt(levelId));

  if (!isAuthenticated()) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-error-box">
            <h2>Please log in to upgrade your level</h2>
            <button onClick={() => navigate('/signin')} className="payment-btn primary">
              Go to Sign In
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!selectedPlan) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-error-box">
            <h2>Invalid level selected</h2>
            <button onClick={() => navigate('/pricing')} className="payment-btn primary">
              Back to Pricing
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const handlePayment = async () => {
    if (selectedPlan.price === 0) {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/upgrade-level`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${useAuthStore.getState().token}`
          },
          body: JSON.stringify({ level: selectedPlan.level })
        });

        if (response.ok) {
          const data = await response.json();
          useAuthStore.getState().setAuth({ ...user, level: data.user.level }, useAuthStore.getState().token);
          navigate('/profile');
        } else {
          const data = await response.json();
          setError(data.error || 'Failed to upgrade level');
        }
      } catch (err) {
        setError('Error upgrading level');
      } finally {
        setLoading(false);
      }
    } else {
      try {
        setLoading(true);
        setError('');

        const response = await fetch(`${process.env.REACT_APP_API_URL}/payment/create-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${useAuthStore.getState().token}`
          },
          body: JSON.stringify({
            level: selectedPlan.level,
            amount: selectedPlan.price,
            paymentMethod: paymentMethod,
            crypto: selectedCrypto,
            email: email
          })
        });

        const data = await response.json();

        if (response.ok) {
          if (paymentMethod === 'card' && data.checkoutUrl) {
            window.location.href = data.checkoutUrl;
          } else if (paymentMethod === 'paypal' && data.paypalUrl) {
            window.location.href = data.paypalUrl;
          } else if (paymentMethod === 'crypto' && data.cryptoUrl) {
            window.location.href = data.cryptoUrl;
          } else {
            setError('Secure checkout is not available yet. Please contact support to upgrade.');
          }
        } else {
          setError(data.message || data.error || 'Payment initialization failed');
        }
      } catch (err) {
        setError('Secure checkout is not available yet. Please contact support to upgrade.');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="payment-page">
      <div className="payment-container">
        <div className="payment-header">
          <h1>Upgrade to {selectedPlan.name}</h1>
          <p>Unlock matching filters for your co-founder search</p>
        </div>

        {/* Payment Method Tabs */}
        {selectedPlan.price > 0 && (
          <div className="payment-tabs">
            <button
              className={`payment-tab ${paymentMethod === 'crypto' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('crypto')}
            >
              ₿ Crypto
            </button>
            <button
              className={`payment-tab ${paymentMethod === 'card' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('card')}
            >
              💳 Card
            </button>
            <button
              className={`payment-tab ${paymentMethod === 'paypal' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('paypal')}
            >
              🅿️ PayPal
            </button>
          </div>
        )}

        {error && <div className="payment-error-message">{error}</div>}

        {/* Crypto Payment */}
        {paymentMethod === 'crypto' && selectedPlan.price > 0 && (
          <div className="payment-form">
            <div className="form-section">
              <label className="form-label">Select Cryptocurrency</label>
              <div className="crypto-grid">
                {cryptoOptions.map(crypto => (
                  <button
                    key={crypto.id}
                    className={`crypto-option ${selectedCrypto === crypto.id ? 'selected' : ''}`}
                    onClick={() => setSelectedCrypto(crypto.id)}
                  >
                    <span className="crypto-icon">{crypto.icon}</span>
                    <div className="crypto-info">
                      <div className="crypto-name">{crypto.name}</div>
                      <div className="crypto-symbol">{crypto.symbol}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-section">
              <label className="form-label">Payment Amount (USD)</label>
              <div className="amount-input">
                <span className="currency">$</span>
                <input
                  type="number"
                  value={selectedPlan.price}
                  readOnly
                  className="form-input"
                />
              </div>
              <p className="amount-hint">Fixed plan price for Level {selectedPlan.level}</p>
            </div>

            <p className="paypal-info">You will be redirected to a secure crypto checkout to complete payment. Card details are never collected on this site.</p>
          </div>
        )}

        {/* Card Payment */}
        {paymentMethod === 'card' && selectedPlan.price > 0 && (
          <div className="payment-form">
            <div className="form-section">
              <label className="form-label">Payment Amount (USD)</label>
              <div className="amount-input">
                <span className="currency">$</span>
                <input
                  type="number"
                  value={selectedPlan.price}
                  readOnly
                  className="form-input"
                />
              </div>
              <p className="amount-hint">Fixed plan price for Level {selectedPlan.level}</p>
            </div>

            <div className="form-section">
              <label className="form-label">Email</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
              />
            </div>

            <p className="paypal-info">
              Card details are entered on a secure hosted checkout page — never on CofounderHubs. Click pay to continue.
            </p>
          </div>
        )}

        {/* PayPal Payment */}
        {paymentMethod === 'paypal' && selectedPlan.price > 0 && (
          <div className="payment-form">
            <div className="form-section">
              <label className="form-label">Payment Amount (USD)</label>
              <div className="amount-input">
                <span className="currency">$</span>
                <input
                  type="number"
                  value={selectedPlan.price}
                  readOnly
                  className="form-input"
                />
              </div>
              <p className="amount-hint">Fixed plan price for Level {selectedPlan.level}</p>
            </div>

            <div className="form-section">
              <label className="form-label">Email</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
              />
            </div>
            <p className="paypal-info">You will be redirected to PayPal to complete your payment securely.</p>
          </div>
        )}

        {/* Payment Summary */}
        {selectedPlan.price > 0 && (
          <div className="payment-summary">
            <div className="summary-row">
              <span>Amount</span>
              <span className="summary-value">${selectedPlan.price.toFixed(2)}</span>
            </div>
            <div className="summary-divider"></div>
            <div className="summary-row total">
              <span>Total</span>
              <span className="summary-value total-value">
                ${selectedPlan.price.toFixed(2)}
              </span>
            </div>
            <div className="summary-row">
              <span>You'll receive</span>
              <span className="summary-value highlight">Level {selectedPlan.level}</span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <button
          onClick={handlePayment}
          disabled={loading}
          className="payment-btn primary full-width"
        >
          {loading ? 'Processing...' : selectedPlan.price === 0 ? 'Upgrade Now' : `Pay $${selectedPlan.price.toFixed(2)}`}
        </button>

        <button
          onClick={() => navigate('/pricing')}
          className="payment-btn secondary full-width"
        >
          Back to Pricing
        </button>
      </div>
      <Footer />
    </div>
  );
}

export default Payment;
